// YouTube URL detection + ID extraction utilities.
//
// Covers the four shapes we see in community-post bodies:
//   https://www.youtube.com/watch?v=ID(&...)
//   https://youtube.com/watch?v=ID
//   https://m.youtube.com/watch?v=ID
//   https://youtu.be/ID(?...)
//   https://www.youtube.com/shorts/ID
//
// IDs are exactly 11 chars from [A-Za-z0-9_-]. Anything else is rejected.

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

// One big alternation. We match the full URL so we can use the same
// regex for both detection (for stripping from rendered body) and
// extraction (collecting IDs).
const YT_URL_RE =
  /https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?[^\s]*?v=([A-Za-z0-9_-]{11})|shorts\/([A-Za-z0-9_-]{11}))|youtu\.be\/([A-Za-z0-9_-]{11}))(?:[^\s]*)?/gi;

export function extractYouTubeVideoIds(text: string): string[] {
  if (!text) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  YT_URL_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = YT_URL_RE.exec(text)) !== null) {
    const id = m[1] || m[2] || m[3];
    if (id && VIDEO_ID_RE.test(id) && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export function buildWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

// Return [strippedText, idsFound]. Removes any matched YouTube URL from
// the body so the preview card replaces the bare link without leaving
// the URL itself in the rendered text. Whitespace is collapsed but
// newlines are preserved.
export function stripYouTubeUrls(text: string, idsToStrip: Set<string>): string {
  if (!text || idsToStrip.size === 0) return text;
  YT_URL_RE.lastIndex = 0;
  const out = text.replace(YT_URL_RE, (match, a, b, c) => {
    const id = a || b || c;
    return id && idsToStrip.has(id) ? '' : match;
  });
  // Collapse runs of spaces/tabs on a single line but keep newlines.
  return out.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+\n/g, '\n').trim();
}
